import L from "leaflet";
import shp from "shpjs";

/**
 * Creates a WMS layer using the Leaflet API.
 * @param {string} url - The URL of the WMS service.
 * @param {string} layers - The layers to include, comma-separated.
 * @param {Object} options - Additional Leaflet WMS options.
 * @returns {L.TileLayer.WMS} The created WMS layer.
 */
export function createWmsLayer(url, layers, options = {}) {
    return L.tileLayer.wms(url, {
        layers: layers,
        format: "image/png",
        transparent: true,
        ...options,
    });
}

/**
 * Fetches and parses available layers from a WMS service.
 * @param {string} url - The URL of the WMS service.
 * @returns {Promise<Array<{name: string, title: string}>>} A promise that resolves to an array of layer objects.
 */
export async function fetchWmsLayers(url) {
    let capabilitiesUrl;
    try {
        capabilitiesUrl = new URL(url);
    } catch (e) {
        throw new Error("URL non válida.");
    }
    capabilitiesUrl.searchParams.set("service", "WMS");
    capabilitiesUrl.searchParams.set("request", "GetCapabilities");
    // We don't force version to let the server decide or use its default

    const response = await fetch(capabilitiesUrl.toString());
    if (!response.ok) {
        throw new Error("Erro ao obter as capacidades do WMS.");
    }

    const xmlText = await response.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    // Check for service exception
    const exception = xmlDoc.querySelector("ServiceException");
    if (exception) {
        throw new Error(exception.textContent.trim());
    }

    const layers = [];
    const layerElements = xmlDoc.getElementsByTagName("Layer");

    for (let i = 0; i < layerElements.length; i++) {
        const layerElement = layerElements[i];
        // We only want layers that have a Name (leaf layers or selectable groups)
        const nameElements = layerElement.getElementsByTagName("Name");
        if (nameElements.length > 0) {
            // Get the Name that is a direct child
            let name = "";
            for (let j = 0; j < nameElements.length; j++) {
                if (nameElements[j].parentElement === layerElement) {
                    name = nameElements[j].textContent;
                    break;
                }
            }

            if (name) {
                const titleElements = layerElement.getElementsByTagName("Title");
                let title = name;
                for (let j = 0; j < titleElements.length; j++) {
                    if (titleElements[j].parentElement === layerElement) {
                        title = titleElements[j].textContent;
                        break;
                    }
                }
                layers.push({name, title});
            }
        }
    }

    return layers;
}

/**
 * Creates a Shapefile layer using the Leaflet API and shpjs.
 * @param {ArrayBuffer} arrayBuffer - The ArrayBuffer of the zip file containing the shapefile.
 * @param {Object} options - Additional Leaflet GeoJSON options.
 * @returns {Promise<L.GeoJSON>} A promise that resolves to the created GeoJSON layer.
 */
export async function createShapefileLayer(arrayBuffer, options = {}) {
    try {
        const geojson = await shp(arrayBuffer);
        return L.geoJSON(geojson, options);
    } catch (error) {
        console.error("Error creating shapefile layer:", error);
        throw error;
    }
}
